import { Component, Input, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import type { ObjetivoVisita } from '../../services/visitas.service';
import { ToastService } from '../../services/toast.service';
import {
  MAX_OBJETIVOS_VISITA_REGISTRO,
  esObjetivoVisitaOpcionDeshabilitada,
  normalizarIdsObjetivoVisita,
} from '../../utils/visita-registro.util';

let selectorObjetivosTriggerSeq = 0;

@Component({
  selector: 'app-selector-objetivos-visita-registro',
  templateUrl: './selector-objetivos-visita-registro.component.html',
  styleUrls: ['./selector-objetivos-visita-registro.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectorObjetivosVisitaRegistroComponent),
      multi: true,
    },
  ],
})
export class SelectorObjetivosVisitaRegistroComponent implements ControlValueAccessor {
  @Input() objetivosVisita: ObjetivoVisita[] = [];
  @Input() max = MAX_OBJETIVOS_VISITA_REGISTRO;
  @Input() placeholder = 'Seleccione temas';

  readonly triggerId = `selector-objetivos-registro-${++selectorObjetivosTriggerSeq}`;

  private readonly toast = inject(ToastService);

  idsSeleccionados: number[] = [];
  isDisabled = false;

  private onChange: (value: number[]) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: number[] | null): void {
    this.idsSeleccionados = normalizarIdsObjetivoVisita(value, this.max);
  }

  registerOnChange(fn: (value: number[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  textoSeleccion(): string {
    return this.idsSeleccionados
      .map((id) => this.objetivosVisita.find((o) => o.id_objetivo_visita === id)?.descripcion?.trim())
      .filter((d): d is string => !!d)
      .join('; ');
  }

  estaSeleccionado(id: number): boolean {
    return this.idsSeleccionados.some((sel) => Number(sel) === Number(id));
  }

  opcionDeshabilitada(id: number): boolean {
    return esObjetivoVisitaOpcionDeshabilitada(id, this.idsSeleccionados, this.max);
  }

  onOpcionClick(id: number, event: Event): void {
    if (this.isDisabled) return;
    if ((event.target as HTMLElement | null)?.closest('ion-checkbox')) return;

    if (!this.estaSeleccionado(id) && this.opcionDeshabilitada(id)) {
      event.preventDefault();
      event.stopPropagation();
      void this.toast.mostrar(`Solo puede seleccionar hasta ${this.max} objetivos.`, 'warning');
      return;
    }

    const next = this.estaSeleccionado(id)
      ? this.idsSeleccionados.filter((sel) => Number(sel) !== Number(id))
      : normalizarIdsObjetivoVisita([...this.idsSeleccionados, id], this.max);
    this.actualizarSeleccion(next);
  }

  onToggle(id: number, event: CustomEvent): void {
    if (this.isDisabled) return;
    const checkbox = event.target as HTMLIonCheckboxElement | null;
    const quiereMarcar = event.detail.checked === true;

    if (quiereMarcar && this.opcionDeshabilitada(id)) {
      if (checkbox) checkbox.checked = false;
      void this.toast.mostrar(`Solo puede seleccionar hasta ${this.max} objetivos.`, 'warning');
      return;
    }

    const next = quiereMarcar
      ? normalizarIdsObjetivoVisita([...this.idsSeleccionados, id], this.max)
      : this.idsSeleccionados.filter((sel) => Number(sel) !== Number(id));

    this.actualizarSeleccion(next);
  }

  private actualizarSeleccion(next: number[]): void {
    this.idsSeleccionados = next;
    this.onChange(next);
    this.onTouched();
  }
}
